import React, { useState } from 'react';
import { TouchableHighlight, StyleSheet, View } from 'react-native';

import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import { get as _get, isNil as _isNil } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import { Icon } from '../Icon';
import { useTheme } from '../Themes';
import { TypesList, Types } from '../Themes/Types';
import VFText from '../VFText';

import styles, { underlayColor } from './VFButton.Styles';

const getProperStyle = (
  type,
  disabled,
  style,
  textStyle,
  disabledStyle,
  disabledTextStyle,
  theme,
  forceTheme,
  isButtonPressedIn,
) => {
  const isThemeForced = _get(forceTheme, 'force', false) && !_isNil(forceTheme.theme);
  const buttonStyles = [
    styles.buttonStyle,
    styles[`${type}ButtonStyle`](theme, disabled),
    isThemeForced ? styles[`${type}ButtonStyle`](forceTheme.theme) : undefined,
    style,
  ];
  const textStyles = [
    styles[`${type}TextStyle`](theme),
    isThemeForced ? styles[`${type}TextStyle`](forceTheme.theme) : undefined,
    textStyle,
  ];
  const underlayColorValue = underlayColor(theme)[type];
  if (disabled) {
    buttonStyles.push(disabledStyle);
    textStyles.push(disabledTextStyle);
  }
  if (type == 'secondary' && isButtonPressedIn) {
    buttonStyles.push(styles.hoveredButton);
    textStyles.push(styles.hoveredText(theme));
  }

  return {
    buttonStyle: StyleSheet.flatten(buttonStyles),
    textStyle: StyleSheet.flatten(textStyles),
    underlayColor: underlayColorValue,
  };
};

const getProperStyleMemoized = memoize(getProperStyle);
const renderIcon = ({ name, type, color, size, iconStyle, containerStyle }, testKey) => {
  return (
    <View style={containerStyle} testID={testKey}>
      <Icon name={name} type={type} color={color} size={size} style={iconStyle} />
    </View>
  );
};

const VFButton = ({
  title,
  onPress,
  disabled,
  style,
  textStyle,
  disabledStyle,
  disabledTextStyle,
  textAccessibilityLabel,
  type,
  placeHolders,
  forceTheme,
  allowFontScaling,
  leftIcon,
  rightIcon,
  testKey,
  testID,
  ...props
}) => {
  const [isButtonPressedIn, setIsButtonPressedIn] = useState(false);
  const theme = useTheme();

  // DEPRECATEME: since [Release 21]
  const _testKey = testKey || testID || textAccessibilityLabel;

  const properStyles = getProperStyleMemoized(
    type,
    disabled,
    style,
    textStyle,
    disabledStyle,
    disabledTextStyle,
    theme,
    forceTheme,
    isButtonPressedIn,
  );

  return (
    <TouchableHighlight
      underlayColor={properStyles.underlayColor}
      onPress={onPress}
      disabled={disabled}
      {...props}
      testID={`${_testKey}Btn`}
      style={properStyles.buttonStyle}
      accessible={false}
      onPressIn={() => {
        setIsButtonPressedIn(true);
      }}
      onPressOut={() => {
        setIsButtonPressedIn(false);
      }}
    >
      <>
        {leftIcon && renderIcon(leftIcon, `${_testKey}LI`)}
        <VFText
          i18nKey={title}
          style={properStyles.textStyle}
          testKey={_testKey}
          placeHolders={placeHolders}
          allowFontScaling={allowFontScaling}
        />
        {rightIcon && renderIcon(rightIcon, `${_testKey}RI`)}
      </>
    </TouchableHighlight>
  );
};

VFButton.defaultProps = {
  type: Types.PRIMARY,
  style: {},
  textStyle: {},
  disabledStyle: {},
  disabledTextStyle: {},
  forceTheme: {
    force: false,
    theme: undefined,
  },
};

renderIcon.propTypes = {
  containerStyle: ViewPropTypes.style,
  ...Icon.propTypes,
};

VFButton.propTypes = {
  type: PropTypes.oneOf(TypesList),
  title: PropTypes.string.isRequired,
  onPress: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  style: TextPropTypes.style,
  textStyle: TextPropTypes.style,
  disabledStyle: ViewPropTypes.style,
  disabledTextStyle: TextPropTypes.style,
  textAccessibilityLabel: PropTypes.string,
  placeHolders: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number])),
    PropTypes.string,
  ]),
  forceTheme: PropTypes.shape({
    force: PropTypes.bool,
    theme: PropTypes.shape({
      name: PropTypes.string,
      isDark: PropTypes.bool,
      colors: PropTypes.object,
    }),
  }),
  leftIcon: PropTypes.objectOf({
    containerStyle: ViewPropTypes.style,
    ...Icon.propTypes,
  }),
  rightIcon: PropTypes.objectOf({
    containerStyle: ViewPropTypes.style,
    ...Icon.propTypes,
  }),
  allowFontScaling: PropTypes.bool,
  testKey: PropTypes.string,
  testID: PropTypes.string,
};

export default VFButton;
